// src/components/common/DownloadCardsContainer.jsx

import React, { useState, useEffect } from 'react';
import apiClient from '../../api/client';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';

// Import gambar-gambar lokal
import ti_minggu from '../../assets/images/minggu.jpg';
import ti_keluarga from '../../assets/images/keluarga.jpg';
import wj_cover from '../../assets/images/wj-cover.jpg';

// Import Heroicons
import { ArrowDownTrayIcon, DocumentTextIcon, CalendarIcon, UsersIcon } from '@heroicons/react/24/outline';

// Komponen DownloadCard yang diintegrasikan ke dalam container
const DownloadCard = ({ title, description, imageUrl, link, icon: Icon }) => (
    <a
        href={link}
        className="block relative w-full h-40 rounded-xl overflow-hidden group shadow-md transition-shadow duration-300 hover:shadow-lg"
    >
        <img
            src={imageUrl}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-start justify-end p-4 transition-all duration-300 group-hover:bg-opacity-70">
            <div className="text-white">
                <div className="flex items-center space-x-2 mb-2">
                    {Icon && <Icon className="h-6 w-6 text-blue-400" />}
                    <h3 className="text-xl font-bold leading-tight">{title}</h3>
                </div>
                <p className="text-sm font-light">{description}</p>
            </div>
            <div className="absolute bottom-4 right-4 text-white opacity-100 transition-opacity duration-300">
                <ArrowDownTrayIcon className="h-7 w-7" />
            </div>
        </div>
    </a>
);

// Komponen utama yang mengambil data dan merender kartu
const DownloadCardsContainer = () => {
    const [downloadContent, setDownloadContent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                setIsLoading(true);
                const response = await apiClient.getHomeContent();
                setDownloadContent({
                    warta_jemaat_cover: {
                        image: wj_cover,
                        pdf: response.warta_jemaat.file,
                    },
                    tata_ibadah_minggu: {
                        image: ti_minggu,
                        pdf: response.ti_minggu.file,
                    },
                    tata_ibadah_keluarga: {
                        image: ti_keluarga,
                        pdf: response.ti_ika.file,
                    },
                });
            } catch (err) {
                console.error("Gagal mengambil konten: ", err);
                setError("Gagal memuat konten. Silakan coba lagi.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchContent();
    }, []);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorDisplay message={error} />;
    }

    if (!downloadContent) {
        return <div className="text-center p-8 text-gray-500">Konten tidak tersedia.</div>;
    }

    const {
        warta_jemaat_cover,
        tata_ibadah_minggu,
        tata_ibadah_keluarga
    } = downloadContent;

    return (
        <div className="space-y-4">
            {warta_jemaat_cover && (
                <DownloadCard
                    title="DOWNLOAD WARTA JEMAAT"
                    description="Informasi dan berita terkini seputar pelayanan jemaat."
                    imageUrl={warta_jemaat_cover.image}
                    link={warta_jemaat_cover.pdf}
                    icon={DocumentTextIcon}
                />
            )}
            {tata_ibadah_minggu && (
                <DownloadCard
                    title="DOWNLOAD TATA IBADAH MINGGU"
                    description="Dapatkan panduan ibadah lengkap untuk persekutuan Minggu."
                    imageUrl={tata_ibadah_minggu.image}
                    link={tata_ibadah_minggu.pdf}
                    icon={CalendarIcon}
                />
            )}
            {tata_ibadah_keluarga && (
                <DownloadCard
                    title="DOWNLOAD TATA IBADAH KELUARGA"
                    description="Panduan tata ibadah untuk persekutuan bersama keluarga."
                    imageUrl={tata_ibadah_keluarga.image}
                    link={tata_ibadah_keluarga.pdf}
                    icon={UsersIcon}
                />
            )}
        </div>
    );
};

export default DownloadCardsContainer;